import { i18n as globalI18n, type I18n, type MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import type { FieldValues, Path, UseFormSetError } from "react-hook-form";

/**
 * Server field name -> form field path. A record overrides single names (the
 * rest still go through snake-to-camel); a function decides every name and
 * returns `undefined` for a field the form does not render.
 */
export type ServerFieldMap =
  | Record<string, string>
  | ((serverField: string) => string | undefined);

// The backend sends validator tags ("required", "max"), never prose
// (handlers/shared/bind.go), so each tag maps to a translated sentence and an
// unknown tag gets the generic one. Raw backend text never reaches a field.
const TAG_MESSAGES: Record<string, MessageDescriptor> = {
  required: msg({ id: "mePortal.field.required", message: "This field is required." }),
  email: msg({ id: "mePortal.field.invalidEmail", message: "Enter a valid email address." }),
  max: msg({ id: "mePortal.field.tooLong", message: "This value is too long." }),
};
const INVALID_MESSAGE = msg({ id: "mePortal.field.invalid", message: "This value is not valid." });

/** `first_name` -> `firstName`, `items[0].unit_price` -> `items.0.unitPrice`. */
export function serverFieldToFormPath(serverField: string): string {
  return serverField
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".")
    .map((segment) => segment.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase()))
    .join(".");
}

/**
 * The rendered form field a server field root names, or undefined. Exact match
 * first; otherwise case- and underscore-insensitive, because some forms name a
 * field `firstname` where the API sends `first_name` (camel-cased `firstName`).
 */
export function resolveFieldName(
  root: string,
  fieldNames: Record<string, unknown>,
): string | undefined {
  if (root in fieldNames) return root;
  const loose = (name: string) => name.replace(/_/g, "").toLowerCase();
  const wanted = loose(root);
  return Object.keys(fieldNames).find((name) => loose(name) === wanted);
}

function fieldErrorsOf(error: unknown): Record<string, unknown> | undefined {
  if (!error || typeof error !== "object" || !("body" in error)) return undefined;
  const body = (error as { body?: unknown }).body;
  if (!body || typeof body !== "object") return undefined;
  const errors = (body as { errors?: unknown }).errors;
  return errors && typeof errors === "object" && !Array.isArray(errors)
    ? (errors as Record<string, unknown>)
    : undefined;
}

/** True when a rejected save names fields in `body.errors`, which a form shows inline. */
export function hasServerFieldErrors(error: unknown): boolean {
  const fieldErrors = fieldErrorsOf(error);
  return fieldErrors !== undefined && Object.keys(fieldErrors).length > 0;
}

/**
 * Marks the fields a rejected save names in `body.errors`
 * (`{"message": "...", "errors": {"field": "tag"}}`) as server errors.
 * Returns true when at least one field was marked, so the caller keeps its
 * toast for errors no field explains.
 */
export function applyServerFieldErrors<T extends FieldValues>(
  setError: UseFormSetError<T>,
  error: unknown,
  fieldMap?: ServerFieldMap,
  i18n: I18n = globalI18n,
): boolean {
  const fieldErrors = fieldErrorsOf(error);
  if (!fieldErrors) return false;

  let matched = false;
  for (const [serverField, tag] of Object.entries(fieldErrors)) {
    const path =
      typeof fieldMap === "function"
        ? fieldMap(serverField)
        : (fieldMap?.[serverField] ?? serverFieldToFormPath(serverField));
    if (!path) continue;
    const descriptor = (typeof tag === "string" && TAG_MESSAGES[tag]) || INVALID_MESSAGE;
    setError(path as Path<T>, { type: "server", message: i18n._(descriptor) });
    matched = true;
  }
  return matched;
}
